<template>
  <div>
    <div class="timeline">
      <template v-for="(state, index) of submittedApplicationStates">
        <div class="timeline-item"
             v-bind:key="state.id"
             :class="timelineItemClass(state.slug, index)">
          <div class="timeline-icon">
            <Icon :icon-name="timelineIconName(state.slug, index)"/>
          </div>
          <div class="timeline-item-details" :class="`status-${state.slug}`">
            <h6>{{ getTitleForStatus(state) }}</h6>
            <div v-if="canBeShown(index)">
              <SubmitApplicationStageComponent v-if="state.slug === 'SUBMIT'" :application="application"/>
              <InReviewStageComponent v-if="state.slug === 'REVIEW'" :application="application" />
              <CancelledStageComponent v-if="['REJECTED', 'WITHDRAWN'].includes(state.slug)" :application="application" />
              <ApprovedStageComponent v-if="state.slug === 'APPROVED'" :application="application" />
              <IdentityVerificationRequiredComponent v-if="state.slug === 'VERIFICATION_REQUIRED'" @on-verification-start="startVerification" :application="application" />
              <IdentityVerificationInProgressComponent v-if="state.slug === 'VERIFICATION_IN_PROGRESS'" :application="application" />
              <IdentityVerificationDoneComponent v-if="state.slug === 'VERIFICATION_DONE'" :application="application" />
              <IdentityVerificationFailedComponent v-if="state.slug === 'VERIFICATION_FAILED'" @on-verification-start="startVerification" :application="application" />
              <IdentityVerificationIsNotSupportedComponent v-if="state.slug === 'VERIFICATION_NOT_SUPPORTED'" />
              <IdentityVerificationIsNotSupportedInDesktopComponent v-if="state.slug === 'VERIFICATION_NOT_SUPPORTED_IN_DESKTOP'" />
              <SigningStageComponent v-if="state.slug === 'SIGNING'" :application="application"/>
              <SignedStageComponent v-if="state.slug === 'SIGNED'" :application="application"/>
              <CollectCashStageComponent v-if="state.slug === 'PAYMENT'" :application="application"/>
              <PaidOutApplicationStageComponent v-if="state.slug === 'COMPLETED'" :application="application"/>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import Icon from '@shared/components/Icon'
import SignedStageComponent from '@shared/components/timeline/SignedStageComponent'
import SigningStageComponent from '@/components/timeline/SigningStageComponent'
import CollectCashStageComponent from '@shared/components/timeline/CollectCashStageComponent'
import SubmitApplicationStageComponent from '@shared/components/timeline/SubmitApplicationStageComponent'
import ApprovedStageComponent from '@shared/components/timeline/ApprovedStageComponent'
import InReviewStageComponent from '@shared/components/timeline/InReviewStageComponent'
import CancelledStageComponent from '@shared/components/timeline/CancelledStageComponent'
import TimelineMixin from '@shared/components/timeline/TimelineMixin'
import PaidOutApplicationStageComponent from '@shared/components/timeline/PaidOutApplicationStageComponent'
import IdentityVerificationDoneComponent from '@shared/components/timeline/IdentityVerificationDoneComponent'
import IdentityVerificationFailedComponent from '@shared/components/timeline/IdentityVerificationFailedComponent'
import IdentityVerificationIsNotSupportedComponent
  from '@shared/components/timeline/IdentityVerificationIsNotSupportedComponent'
import IdentityVerificationInProgressComponent
  from '@shared/components/timeline/IdentityVerificationInProgressComponent'
import IdentityVerificationRequiredComponent from '@shared/components/timeline/IdentityVerificationRequiredComponent'
import settings from '@shared/settings'
import GetIDMixin from '@shared/mixins/GetIDMixin'
import IdentityVerificationIsNotSupportedInDesktopComponent
  from '@shared/components/timeline/IdentityVerificationIsNotSupportedInDesktopComponent'

export default {
  name: 'RegularLoanApplicationWithoutVerificationTimeline',
  components: {
    IdentityVerificationRequiredComponent,
    IdentityVerificationInProgressComponent,
    IdentityVerificationDoneComponent,
    IdentityVerificationFailedComponent,
    IdentityVerificationIsNotSupportedComponent,
    IdentityVerificationIsNotSupportedInDesktopComponent,
    PaidOutApplicationStageComponent,
    Icon,
    SignedStageComponent,
    SigningStageComponent,
    CollectCashStageComponent,
    SubmitApplicationStageComponent,
    ApprovedStageComponent,
    InReviewStageComponent,
    CancelledStageComponent
  },
  props: ['application', 'showFuture'],
  mixins: [TimelineMixin, GetIDMixin],
  data() {
    return {
      titles: {
        SUBMIT: 'SUBMIT',
        REVIEW: 'REVIEW',
        APPROVED: 'REVIEW',
        VERIFICATION_REQUIRED: 'VERIFICATION',
        VERIFICATION_IN_PROGRESS: 'VERIFICATION',
        VERIFICATION_FAILED: 'VERIFICATION',
        VERIFICATION_DONE: 'VERIFICATION',
        VERIFICATION_NOT_SUPPORTED: 'VERIFICATION',
        VERIFICATION_NOT_SUPPORTED_IN_DESKTOP: 'VERIFICATION',
        SIGNING: 'APPROVED',
        SIGNED: 'APPROVED',
        PAYMENT: 'SIGNED',
        COMPLETED: 'COMPLETED',
        REJECTED: 'REJECTED',
        WITHDRAWN: 'WITHDRAWN'
      },

      timelineSteps: {
        REVIEW: {
          standard: ['SUBMIT', 'REVIEW'],
          next: ['VERIFICATION_REQUIRED', 'SIGNING', 'PAYMENT']
        },
        REJECTED: {
          standard: ['SUBMIT', 'REJECTED'],
          next: []
        },
        WITHDRAWN: {
          standard: ['SUBMIT', 'WITHDRAWN'],
          next: []
        },
        VERIFICATION_REQUIRED: {
          standard: ['SUBMIT', 'APPROVED', 'VERIFICATION_REQUIRED'],
          next: ['SIGNING', 'PAYMENT']
        },
        VERIFICATION_IN_PROGRESS: {
          standard: ['SUBMIT', 'APPROVED', 'VERIFICATION_IN_PROGRESS'],
          next: ['SIGNING', 'PAYMENT']
        },
        VERIFICATION_FAILED: {
          standard: ['SUBMIT', 'APPROVED', 'VERIFICATION_FAILED'],
          next: ['SIGNING', 'PAYMENT']
        },
        VERIFICATION_NOT_SUPPORTED: {
          standard: ['SUBMIT', 'APPROVED', 'VERIFICATION_NOT_SUPPORTED'],
          next: ['SIGNING', 'PAYMENT']
        },
        VERIFICATION_NOT_SUPPORTED_IN_DESKTOP: {
          standard: ['SUBMIT', 'APPROVED', 'VERIFICATION_NOT_SUPPORTED_IN_DESKTOP'],
          next: ['SIGNING', 'PAYMENT']
        },
        SIGNING: {
          standard: ['SUBMIT', 'APPROVED', 'VERIFICATION_DONE', 'SIGNING'],
          next: ['PAYMENT']
        },
        PAYMENT: {
          standard: ['SUBMIT', 'APPROVED', 'VERIFICATION_DONE', 'SIGNED', 'PAYMENT'],
          next: []
        },
        COMPLETED: {
          standard: ['SUBMIT', 'APPROVED', 'VERIFICATION_DONE', 'SIGNED', 'COMPLETED'],
          next: []
        }
      }
    }
  },
  computed: {
    isOnlineVerificationApplicable() {
      return this.application.onlineVerificationApplicable && settings.isGetIdSupported() && settings.featureFlags.isIdentityVerificationEnabled()
    },
    submittedApplicationStates() {
      const steps = this.maskUnsupportedVerificationSteps(this.getDefaultSteps())
      return steps.map(step => {
        return {
          slug: step,
          title: this.titles[step]
        }
      })
    }
  },
  methods: {
    startVerification() {
      const flowName = this.$configuration.newCoreOnlineVerificationEnabled ? this.$configuration.newCoreOnlineVerificationFlow : undefined
      this.startGetID({
        flowName,
        reference: this.application.reference
      })
    },
    maskUnsupportedVerificationSteps(originalSteps) {
      if (this.isOnlineVerificationApplicable) {
        return originalSteps
      }

      if (!settings.isGetIdSupported() && settings.featureFlags.isIdentityVerificationEnabled()) {
        return originalSteps
            .map(step => step.startsWith('VERIFICATION_') ? this.getVerificationNotSupportedState() : step)
            // Make sure each step is present only once
            .filter((step, index, self) => self.indexOf(step) === index)
      } else {
        return originalSteps.filter(step => !step.startsWith('VERIFICATION_'))
      }
    },
    getDefaultSteps() {
      const path = this.timelineSteps[this.application.status]
      let steps = [...path.standard]
      if (this.showFuture) {
        steps = steps.concat(path.next)
      }
      return steps
    },
    canBeShown(index) {
      return index <= this.currentStateIndex
    },
    onVerificationComplete({ verificationId, reference }) {
      if (!!this.application.reference && reference !== this.application.reference) return
      this.$set(this.application, 'status', 'VERIFICATION_IN_PROGRESS')
      this.$forceUpdate()

      this.saveVerificationId(verificationId, reference)
    },
    saveVerificationId(verificationId, reference) {
      this.$http.post(`/api/applications/${reference}/verification-completed`, {
        verificationId
      })
    },
    getVerificationNotSupportedState() {
      if (settings.isDesktopWeb()) {
        return 'VERIFICATION_NOT_SUPPORTED_IN_DESKTOP'
      } else {
        return 'VERIFICATION_NOT_SUPPORTED'
      }
    },
    correctVerificationStatus() {
      if (this.application.status.startsWith('VERIFICATION_')) {
        if (!settings.featureFlags.isIdentityVerificationEnabled()) {
          this.$set(this.application, 'status', 'SIGNING')
        } else if (!settings.isGetIdSupported() || settings.isDesktopWeb()) {
          this.$set(this.application, 'status', this.getVerificationNotSupportedState())
        }
      }
    },
    correctDealerLoanStatus() {
      if (this.application.productType === 'DEALER' && this.application.status === 'PAYMENT') {
        this.$set(this.application, 'status', 'COMPLETED')
      }
    }
  },
  created() {
    this.correctVerificationStatus()
    this.correctDealerLoanStatus()
    this.$root.$on('verification-completed', this.onVerificationComplete)
  },
  updated() {
    this.correctVerificationStatus()
    this.correctDealerLoanStatus()
  },
  destroyed() {
    this.$root.$off('verification-completed', this.onVerificationComplete)
  }
}
</script>
